import { useQuery } from "@tanstack/react-query";

import { AASpinner, CardGrid, Card } from "@bagel-web/components";

import { agentQueries } from "../queries";
import { AgentCloneTree, CommonAPI } from "../types";
import AgentCloneTimeline from "./AgentCloneTimeline";
import { Alert, Table } from "react-bootstrap";
import SessionLink from "../session/SessionLink";
import AgentLink from "./AgentLink";

const ChildAgentRow = ({
  child,
  commonAPI,
  depth = 0,
}: {
  child: AgentCloneTree;
  commonAPI: CommonAPI;
  depth?: number;
}) => {
  return (
    <>
      <tr key={child.session_id}>
        <td>
          <div style={{ paddingLeft: `${depth * 20}px` }}>
            <SessionLink sessionId={child.session_id} commonAPI={commonAPI} />
          </div>
        </td>
        <td>{child.moment_id}</td>
        <td>
          <AgentLink
            sessionId={child.session_id}
            agentId={child.agent_id}
            commonAPI={commonAPI}
            linkText={"Agent Details"}
          />
        </td>
      </tr>
      {child.children?.map((grandchild) => (
        <ChildAgentRow
          key={grandchild.session_id}
          child={grandchild}
          commonAPI={commonAPI}
          depth={depth + 1}
        />
      ))}
    </>
  );
};

export const getAncestorAgents = (
  cloneTree?: AgentCloneTree
): AgentCloneTree[] => {
  const ancestorAgents = [];
  let node = cloneTree;
  while (node?.is_ancestor) {
    ancestorAgents.push(node);
    // Each node on the ancestral path only has one child.
    node = node.children?.[0];
  }
  return ancestorAgents;
};

export const getCurrentAgent = (cloneTree?: AgentCloneTree) => {
  let node = cloneTree;
  while (node?.is_ancestor) {
    node = node.children?.[0];
  }
  return node;
};

const AgentCloneHistory = ({
  sessionId,
  agentId,
  commonAPI,
}: {
  sessionId: string;
  agentId: string;
  commonAPI: CommonAPI;
}) => {
  const {
    data: cloneTree,
    isPending: isPendingCloneTree,
    isError: isErrorCloneTree,
    error: errorCloneTree,
  } = useQuery({
    queryKey: agentQueries.getCloneTree(sessionId, agentId),
    queryFn: () => commonAPI.fetchAgentCloneTree({ sessionId, agentId }),
  });

  if (isPendingCloneTree) {
    return <AASpinner />;
  }

  if (isErrorCloneTree) {
    return (
      <Alert variant="warning">
        Error loading agent clone tree:{" "}
        {errorCloneTree?.message ?? errorCloneTree}
      </Alert>
    );
  }

  const agentAncestors = getAncestorAgents(cloneTree);
  const currentAgent = getCurrentAgent(cloneTree);

  if (!agentAncestors.length && !currentAgent?.children?.length) {
    return <div className="mt-3">Agent has no parent or clones.</div>;
  }

  return (
    <CardGrid maxColumns={1} className="mt-3">
      {cloneTree && (
        <Card title="Clone Timeline">
          {
            <AgentCloneTimeline
              sessionId={sessionId}
              agentId={agentId}
              cloneTree={cloneTree}
            />
          }
        </Card>
      )}

      {agentAncestors.length > 0 && (
        <Card title="Ancestor Agents">
          <Table className="mb-4">
            <thead>
              <tr>
                <th>Session ID</th>
                <th>First Moment</th>
                <th>Ancestor Agent</th>
              </tr>
            </thead>
            <tbody>
              {agentAncestors.map((ancestor) => (
                <tr key={ancestor.session_id}>
                  <td>
                    <SessionLink
                      sessionId={ancestor.session_id}
                      commonAPI={commonAPI}
                    />
                  </td>
                  <td>{ancestor.moment_id}</td>
                  <td>
                    <AgentLink
                      sessionId={ancestor.session_id}
                      agentId={ancestor.agent_id}
                      commonAPI={commonAPI}
                      linkText={"Agent Details"}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {currentAgent?.children?.length !== undefined &&
        currentAgent.children.length > 0 && (
          <Card title="Child Agents">
            <Table className="mb-4">
              <thead>
                <tr>
                  <th>Session ID</th>
                  <th>First Moment</th>
                  <th>Child Agent</th>
                </tr>
              </thead>
              <tbody>
                {currentAgent?.children?.map((child) => (
                  <ChildAgentRow
                    key={child.session_id}
                    child={child}
                    commonAPI={commonAPI}
                  />
                ))}
              </tbody>
            </Table>
          </Card>
        )}
    </CardGrid>
  );
};

export default AgentCloneHistory;
